import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { settingsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

// Built-in defaults = what every company saw before Phase 5 (no MPN column, sorted by name).
export const INVENTORY_VIEWS = {
  warehouse: {
    columns: [
      { key: 'item_name', label: 'Item Name', locked: true },
      { key: 'sku', label: 'Internal SKU' },
      { key: 'manufacturer_part_number', label: 'Manufacturer Part #' },
      { key: 'location_label', label: 'Location' },
      { key: 'categories', label: 'Categories' },
      { key: 'qty_on_hand', label: 'On Hand' },
      { key: 'available_qty', label: 'Available', locked: true },
      { key: 'allocated_qty', label: 'Allocated' },
      { key: 'reserved_qty', label: 'Reserved' },
      { key: 'quarantined_qty', label: 'Quarantined' },
      { key: 'effective_reorder_threshold', label: 'Threshold' },
    ],
    defaults: ['item_name', 'sku', 'location_label', 'categories', 'qty_on_hand', 'available_qty', 'allocated_qty', 'reserved_qty', 'quarantined_qty', 'effective_reorder_threshold'],
  },
  vehicle: {
    columns: [
      { key: 'item_name', label: 'Item Name', locked: true },
      { key: 'sku', label: 'Internal SKU' },
      { key: 'manufacturer_part_number', label: 'Manufacturer Part #' },
      { key: 'qty_on_hand', label: 'On Hand', locked: true },
      { key: 'available_qty', label: 'Available' },
      { key: 'quarantined_qty', label: 'Quarantined' },
      { key: 'effective_reorder_threshold', label: 'Threshold' },
    ],
    defaults: ['item_name', 'sku', 'qty_on_hand', 'available_qty', 'quarantined_qty', 'effective_reorder_threshold'],
  },
};

const BUILTIN_SORT = { sort_key: 'item_name', sort_dir: 'asc' };
const cache = {}; // per signed-in user: { company, personal, can_edit }

export function useInventoryView(view) {
  const { user } = useAuth();
  const ck = `${user?.tenant_id || 'none'}:${user?.id || 'none'}`;
  const [data, setData] = useState(cache[ck] || null);
  const [loaded, setLoaded] = useState(!!cache[ck]);

  useEffect(() => {
    if (cache[ck]) { setData(cache[ck]); return; }
    settingsAPI.getInventoryDisplay()
      .then(r => { cache[ck] = r.data; setData(r.data); })
      .catch(() => setData({ company: {}, personal: {}, can_edit: false }))
      .finally(() => setLoaded(true));
  }, [ck]);

  const companyView = data?.company?.[view] || null;
  const personal = data?.personal?.[view] || null;
  const base = companyView || { columns: INVENTORY_VIEWS[view].defaults, ...BUILTIN_SORT };
  const effective = { ...base, ...(personal || {}) };
  const locked = useMemo(() => INVENTORY_VIEWS[view].columns.filter(c => c.locked).map(c => c.key), [view]);
  const columns = useMemo(() => [...new Set([...locked, ...effective.columns])], [locked, effective.columns]);

  const setPersonal = useCallback((value) => {
    const next = { ...(cache[ck] || data || {}), personal: { ...((cache[ck] || data)?.personal || {}), [view]: value } };
    cache[ck] = next;
    setData(next);
  }, [ck, data, view]);

  // Saved on the server against this user only, so it follows them across devices.
  const savePersonal = useCallback(async (patch) => {
    const prev = personal;
    const next = { columns, sort_key: effective.sort_key, sort_dir: effective.sort_dir, ...patch };
    setPersonal(next);
    try {
      await settingsAPI.saveMyInventoryView(view, next);
    } catch (err) {
      setPersonal(prev);
      toast.error(err.response?.data?.detail || 'Could not save your view');
    }
  }, [personal, columns, effective.sort_key, effective.sort_dir, setPersonal, view]);

  const toggleColumn = (col) => savePersonal({ columns: columns.includes(col) ? columns.filter(c => c !== col) : [...columns, col] });
  const setSort = (sortKey) => savePersonal({
    sort_key: sortKey,
    sort_dir: effective.sort_key === sortKey && effective.sort_dir === 'asc' ? 'desc' : 'asc',
  });
  const resetToCompany = async () => {
    const prev = personal;
    setPersonal(null);
    try { await settingsAPI.resetMyInventoryView(view); } catch { setPersonal(prev); toast.error('Could not reset your view'); }
  };
  const saveAsCompany = async () => {
    try {
      const r = await settingsAPI.updateInventoryDisplay({ [view]: { columns, sort_key: effective.sort_key, sort_dir: effective.sort_dir } });
      const next = { ...(cache[ck] || data || {}), company: r.data.company };
      cache[ck] = next;
      setData(next);
      await resetToCompany();
      toast.success('Saved as the company default for everyone');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save the company default');
    }
  };

  return {
    loaded, columns, isVisible: (c) => columns.includes(c), sortKey: effective.sort_key, sortDir: effective.sort_dir,
    toggleColumn, setSort, setSortExact: (sort_key, sort_dir) => savePersonal({ sort_key, sort_dir }), resetToCompany, saveAsCompany, canEdit: !!data?.can_edit,
    isCustomized: !!personal, hasCompanyDefault: !!companyView,
  };
}
